import { useState, useLayoutEffect, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { v4 as uuidv4 } from 'uuid'
import AddFaultVideoModal from './AddFaultVideoModal'
import Dropdown from '../../common/Dropdown'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import { toast } from '../../common/Toast'
import CheckboxInput from '../../common/CheckboxInput'
import ExpandableSection from '../../common/ExpandableSection'
import SimpleTextInput from '../../common/SimpleTextInput'
import WrappedTabs from '../../common/WrappedTabs'
import VideoModal from '../../common/VideoModal'
import TooltipedIcon from '../../common/TooltipedIcon'
import ToggleSwitch from '../../common/ToggleSwitch'
import { AddSimpleIcon, InformationIcon, TrashCanFull, VideoPlayIcon } from '../../../icons'
import { languagesShort } from '../../../utils/languages'
import { troubleshootingUploadWritePermission } from '../../../utils/permissionValidation'
import { keyboardEvents } from '../../../utils/keyboardEvents'
import { validateFaultModels } from '../../../utils/requests/troubleshootingAPI'
import { createFault, updateFault } from '../../../actions/troubleshooting'
import '../uploads.scss'

const AddEditFaultModal = (props) => {
  const { t } = useTranslation()
  const { selectedFault, closeModal, showSpinner, setShowSpinner } = props
  const technicianTabId = 'tech'
  const userTabId = 'dentist'
  const languagesWrappedTabsId = 'languages-wrapped-tabs'
  const englishLanguageCode = 'EN'
  const unkownLanguageCode = 'ZZ'
  const [selectedModels, setSelectedModels] = useState([])
  const [faultName, setFaultName] = useState('')
  const [languages, setLanguages] = useState([])
  const [status, setStatus] = useState(false)
  const [initialLanguages, setInitialLanguages] = useState(null)
  const [selectedVideos, setSelectedVideos] = useState([null, null, null])
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [modelsOptions, setModelsOptions] = useState([])
  const [selectedLanguageTab, setSelectedLanguageTab] = useState(technicianTabId)
  const [expandedItem, setExpandedItem] = useState(null)
  const [showAddVideoModal, setShowAddVideoModal] = useState(false)
  const [haveError, setHaveError] = useState({})
  const [invalidModels, setInvalidModels] = useState([])
  const Statuses = ['inactive', 'active']

  useLayoutEffect(() => {
    const sortedModels = props.models.sort((a, b) => a?.localeCompare(b))
    setModelsOptions(sortedModels)
  }, [props.models])

  useLayoutEffect(() => {
    if (selectedFault) {
      setFaultName(selectedFault.fault || '')
      setSelectedModels(selectedFault.models || [])
      setStatus(selectedFault.isActive)

      const videos = selectedFault.videos || []
      const videosWithNulls = [...videos, null, null, null].slice(0, 3)
      setSelectedVideos(videosWithNulls)

      const mappedLanguages = []
      const techUserGroupId = props.faultUserGroups.find((userGroup) => userGroup.name === technicianTabId)?.id
      const userUserGroupId = props.faultUserGroups.find((userGroup) => userGroup.name === userTabId)?.id

      const definitionLanguages = [...new Set(selectedFault.definitions?.map((definition) => definition.languageCode))]

      definitionLanguages.forEach((lang) => {
        const langDefinitions = selectedFault.definitions?.filter((definition) => definition.languageCode === lang)

        const technicianDescriptions = langDefinitions
          .find((definition) => definition.userGroupId === techUserGroupId)
          ?.descriptions?.map((desc) => ({ id: uuidv4(), value: desc })) || [{ id: uuidv4(), value: '' }]
        const technicianProbableCauses = langDefinitions
          .find((definition) => definition.userGroupId === techUserGroupId)
          ?.probableCause?.map((cause) => ({ id: uuidv4(), value: cause })) || [{ id: uuidv4(), value: '' }]
        const technicianTechTroubleshootings = langDefinitions
          .find((definition) => definition.userGroupId === techUserGroupId)
          ?.techTroubleshooting?.map((troubleshoot) => ({ id: uuidv4(), value: troubleshoot })) || [{ id: uuidv4(), value: '' }]
        const technicianUserTroubleshootings = langDefinitions
          .find((definition) => definition.userGroupId === techUserGroupId)
          ?.userTroubleshooting?.map((troubleshoot) => ({ id: uuidv4(), value: troubleshoot })) || [{ id: uuidv4(), value: '' }]

        const userDescriptions = langDefinitions
          .find((definition) => definition.userGroupId === userUserGroupId)
          ?.descriptions?.map((desc) => ({ id: uuidv4(), value: desc })) || [{ id: uuidv4(), value: '' }]
        const userProbableCauses = langDefinitions
          .find((definition) => definition.userGroupId === userUserGroupId)
          ?.probableCause?.map((cause) => ({ id: uuidv4(), value: cause })) || [{ id: uuidv4(), value: '' }]
        const userTroubleshootings = langDefinitions
          .find((definition) => definition.userGroupId === userUserGroupId)
          ?.userTroubleshooting?.map((troubleshoot) => ({ id: uuidv4(), value: troubleshoot })) || [{ id: uuidv4(), value: '' }]

        const mappedLanguage = {
          code: lang,
          ids: langDefinitions.map((definition) => definition.id),
          [technicianTabId]: {
            descriptions: technicianDescriptions.length ? technicianDescriptions : [{ id: uuidv4(), value: '' }],
            probableCauses: technicianProbableCauses.length ? technicianProbableCauses : [{ id: uuidv4(), value: '' }],
            techTroubleshootings: technicianTechTroubleshootings.length ? technicianTechTroubleshootings : [{ id: uuidv4(), value: '' }],
            userTroubleshootings: technicianUserTroubleshootings.length ? technicianUserTroubleshootings : [{ id: uuidv4(), value: '' }],
          },
          [userTabId]: {
            descriptions: userDescriptions.length ? userDescriptions : [{ id: uuidv4(), value: '' }],
            probableCauses: userProbableCauses.length ? userProbableCauses : [{ id: uuidv4(), value: '' }],
            userTroubleshootings: userTroubleshootings.length ? userTroubleshootings : [{ id: uuidv4(), value: '' }],
          },
        }

        mappedLanguages.push(mappedLanguage)
      })

      setInitialLanguages(JSON.stringify(mappedLanguages))
      setLanguages(mappedLanguages)
      return
    }

    setLanguages([
      {
        code: englishLanguageCode,
        [technicianTabId]: {
          descriptions: [{ id: uuidv4(), value: '' }],
          probableCauses: [{ id: uuidv4(), value: '' }],
          techTroubleshootings: [{ id: uuidv4(), value: '' }],
          userTroubleshootings: [{ id: uuidv4(), value: '' }],
        },
        [userTabId]: {
          descriptions: [{ id: uuidv4(), value: '' }],
          probableCauses: [{ id: uuidv4(), value: '' }],
          userTroubleshootings: [{ id: uuidv4(), value: '' }],
        },
      },
    ])
  }, [selectedFault])

  useEffect(() => {
    const hasDifferentModels = selectedModels.some((model) => !selectedFault?.models?.includes(model))
    if (hasDifferentModels && faultName.trim().length > 0) {
      handleCheckValidFaultName()
    }
  }, [selectedModels])

  const faultHeaderContent = () => {
    return (
      <div className="status-wrapper">
        <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('troubleshooting_context.status_toggle_text')}`} orientation="bottom" />
        <div className="label">{t('status')}</div>
        <ToggleSwitch checked={status} onChange={() => setStatus((prevValue) => !prevValue)} isDisabled={showSpinner} />
        <div className="status">{t(Statuses[Number(status)])}</div>
      </div>
    )
  }

  const faultFooterContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={closeModal}>
          {t('cancel')}
        </SimpleButton>
        <SimpleButton
          className="submit-button"
          onClick={handleAddEditFault}
          disabled={showSpinner || hasSameInput()}
          requiredPermission={troubleshootingUploadWritePermission}
        >
          {t('save')}
        </SimpleButton>
      </>
    )
  }

  const hasSameInput = () => {
    return (
      selectedFault &&
      selectedFault.fault === faultName.trim() &&
      selectedFault.models?.length === selectedModels.length &&
      status === selectedFault.isActive &&
      selectedModels.every((model) => selectedFault.models.includes(model)) &&
      selectedFault.videos?.length === selectedVideos.filter((v) => v).length &&
      selectedVideos.filter((v) => v).every((video) => selectedFault.videos?.some((v) => v?.id === video?.id)) &&
      languages.length === [...new Set(selectedFault.definitions?.map((definition) => definition.languageCode))].length &&
      JSON.stringify(languages) === initialLanguages
    )
  }

  const handleCheckValidFaultName = async () => {
    if (faultName && faultName.trim().length > 0) {
      try {
        setShowSpinner(true)
        const faultNameValidationResponse = await validateFaultModels({
          fault: faultName.trim(),
          model: selectedModels.filter((model) => !selectedFault?.models?.includes(model)),
        })

        if (faultNameValidationResponse.length) {
          setInvalidModels(faultNameValidationResponse.map((fault) => fault.model))

          setHaveError((prevError) => ({
            ...prevError,
            faultName: true,
          }))

          return
        }
      } catch (error) {
        toast.error(t('error.something_went_wrong'))
      } finally {
        setShowSpinner(false)
      }
    }

    setInvalidModels([])
    setHaveError((prevError) => ({
      ...prevError,
      faultName: false,
    }))
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (selectedModels.length === 0) {
      errors = { ...errors, models: true }
      isValid = false
    }

    if (!faultName.trim()) {
      errors = { ...errors, faultName: true }
      isValid = false
    }

    if (selectedModels.length && selectedModels.some((model) => invalidModels.includes(model))) {
      errors = { ...errors, faultName: true }
      isValid = false
    }

    if (languages.find((l) => l.code === unkownLanguageCode)) {
      isValid = false
    }

    setHaveError(errors)
    return isValid
  }

  const handleAddEditFault = async () => {
    if (!isValidInput()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    const faultDefinitions = []

    languages.forEach((lang) => {
      faultDefinitions.push({
        languageCode: lang.code,
        ...(lang.ids?.length && { id: lang.ids[0] }),
        userGroupId: props.faultUserGroups.find((userGroup) => userGroup.name === technicianTabId)?.id,
        descriptions: lang[technicianTabId].descriptions.filter((desc) => desc.value.trim()).map((desc) => desc.value),
        probableCause: lang[technicianTabId].probableCauses.filter((cause) => cause.value.trim()).map((cause) => cause.value),
        techTroubleshooting: lang[technicianTabId].techTroubleshootings
          .filter((troubleshoot) => troubleshoot.value.trim())
          .map((troubleshoot) => troubleshoot.value),
        userTroubleshooting: lang[technicianTabId].userTroubleshootings
          .filter((troubleshoot) => troubleshoot.value.trim())
          .map((troubleshoot) => troubleshoot.value),
      })

      faultDefinitions.push({
        languageCode: lang.code,
        ...(lang.ids?.length > 1 && { id: lang.ids[1] }),
        userGroupId: props.faultUserGroups.find((userGroup) => userGroup.name === userTabId)?.id,
        descriptions: lang[userTabId].descriptions.filter((desc) => desc.value.trim()).map((desc) => desc.value),
        probableCause: lang[userTabId].probableCauses.filter((cause) => cause.value.trim()).map((cause) => cause.value),
        userTroubleshooting: lang[userTabId].userTroubleshootings.filter((troubleshoot) => troubleshoot.value.trim()).map((troubleshoot) => troubleshoot.value),
      })
    })

    const bodyParams = {
      models: selectedModels,
      isActive: status,
      fault: faultName.trim(),
      definitions: faultDefinitions,
      videos: selectedVideos.filter((video) => video).map((v) => v.id),
    }

    if (selectedFault) {
      props.actions.updateFault(selectedFault.id, bodyParams).then(() => {
        closeModal()
      })
      return
    }

    props.actions.createFault(bodyParams).then(() => {
      closeModal()
    })
  }

  const addLanguage = () => {
    if (languages.find((l) => l.code === unkownLanguageCode)) {
      toast.error(t('error.language'))
      return
    }

    setLanguages([
      ...languages,
      {
        code: unkownLanguageCode,
        [technicianTabId]: {
          descriptions: [{ id: uuidv4(), value: '' }],
          probableCauses: [{ id: uuidv4(), value: '' }],
          techTroubleshootings: [{ id: uuidv4(), value: '' }],
          userTroubleshootings: [{ id: uuidv4(), value: '' }],
        },
        [userTabId]: {
          descriptions: [{ id: uuidv4(), value: '' }],
          probableCauses: [{ id: uuidv4(), value: '' }],
          userTroubleshootings: [{ id: uuidv4(), value: '' }],
        },
      },
    ])
  }

  const updateLanguage = (code, updatedFields) => {
    const updatedLanguages = languages.map((lang) => (lang.code === code ? { ...lang, ...updatedFields } : lang))
    setLanguages(updatedLanguages)
  }

  const addLanguageField = (code, field) => {
    const updatedLanguages = languages.map((lang) => {
      if (lang.code === code) {
        return {
          ...lang,
          [selectedLanguageTab]: {
            ...lang[selectedLanguageTab],
            [field]: [...lang[selectedLanguageTab][field], { id: uuidv4(), value: '' }],
          },
        }
      }
      return lang
    })
    setLanguages(updatedLanguages)
  }

  const removeLanguageField = (code, field, id) => {
    const updatedLanguages = languages.map((lang) => {
      if (lang.code === code) {
        return {
          ...lang,
          [selectedLanguageTab]: {
            ...lang[selectedLanguageTab],
            [field]:
              lang[selectedLanguageTab][field].filter((item) => item.id !== id).length > 0
                ? lang[selectedLanguageTab][field].filter((item) => item.id !== id)
                : [{ id: uuidv4(), value: '' }],
          },
        }
      }
      return lang
    })
    setLanguages(updatedLanguages)
  }

  const updateLanguageField = (code, field, id, value) => {
    const updatedLanguages = languages.map((lang) => {
      if (lang.code === code) {
        return {
          ...lang,
          [selectedLanguageTab]: {
            ...lang[selectedLanguageTab],
            [field]: lang[selectedLanguageTab][field].map((item) => (item.id === id ? { ...item, value } : item)),
          },
        }
      }
      return lang
    })
    setLanguages(updatedLanguages)
  }

  const handleCopyTechnicianFieldsToEndUser = (languageCode) => {
    const updatedLanguages = languages.map((lang) => {
      if (lang.code === languageCode) {
        return {
          ...lang,
          [userTabId]: {
            ...lang[userTabId],
            descriptions: lang[technicianTabId].descriptions.map((desc) => ({ id: uuidv4(), value: desc.value })),
            probableCauses: lang[technicianTabId].probableCauses.map((cause) => ({ id: uuidv4(), value: cause.value })),
            userTroubleshootings: lang[technicianTabId].userTroubleshootings.map((troubleshoot) => ({
              id: uuidv4(),
              value: troubleshoot.value,
            })),
          },
        }
      }
      return lang
    })
    setLanguages(updatedLanguages)
  }

  const toggleExpand = (index) => {
    setExpandedItem(expandedItem === index ? null : index)
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={closeModal}
        className="uploads-modal"
        title={selectedFault ? t('troubleshoot.edit_fault') : t('troubleshoot.add_fault')}
        headerContent={faultHeaderContent()}
        footerContent={faultFooterContent()}
      >
        <div className="uploads-modal-content">
          <div className="section-title">{t('troubleshoot.fault_setup').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option flex-justify-start">
                {t('monitoring.models')}
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('troubleshooting_context.select_model')}`} orientation="right" />
              </p>
              {haveError['models'] && <span className="error-message">{t('error.must_select_one_model')}</span>}
              <div className="ordered-list-wrapper">
                <div className="columns-wrapper">
                  {modelsOptions.map((model, index) => (
                    <div className="item-wrapper" key={index}>
                      <CheckboxInput
                        checked={selectedModels.includes(model)}
                        text={model}
                        onChange={() =>
                          setSelectedModels((prevValue) => (prevValue.includes(model) ? prevValue.filter((value) => value !== model) : [...prevValue, model]))
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="inner-separator" />
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option flex-justify-start">
                {`${t('troubleshoot.fault_name')}*`}
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('troubleshooting_context.fault_name_info')}`} orientation="extra-right" />
              </p>
              <SimpleTextInput
                value={faultName}
                hasError={haveError['faultName']}
                onChange={(value) => setFaultName(value)}
                onClear={() => setFaultName('')}
                onBlur={handleCheckValidFaultName}
                disabled={selectedModels.length === 0 || selectedFault}
              />
              {invalidModels.length > 0 && (
                <div className="small-disclaimer">
                  <span>{`${t('troubleshoot.fault_already_associated')} ${invalidModels.join(', ')}`}</span>
                </div>
              )}
            </div>
            <div className="input-wrapper" />
          </div>
          <div className="separator" />
          <div className="section-title d-flex flex-justify-start flex-align-center">
            {t('languages').toUpperCase()}
            <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('troubleshooting_context.tabs_info')}`} orientation="extra-right" />
          </div>
          <div className="row-wrapper">
            <WrappedTabs
              containerId={languagesWrappedTabsId}
              tabs={[
                { id: technicianTabId, name: 'technician' },
                { id: userTabId, name: 'end_user' },
              ]}
              activeTab={selectedLanguageTab}
              changeTab={(languageTab) => setSelectedLanguageTab(languageTab)}
            />
          </div>
          {languages.map((language, index) => (
            <div className="row-wrapper" key={index}>
              <ExpandableSection
                title={`${t(languagesShort[language.code.toLowerCase()] || language.code)} (${language.code})`}
                isExpanded={expandedItem === index}
                onToggle={() => toggleExpand(index)}
                className="with-content-expandable large-body-spacing"
                additionalContent={
                  <div className="expandable-action">
                    {selectedLanguageTab !== technicianTabId && (
                      <label
                        className="action-label submit-label d-flex flex-align-center flex-justify-start"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleCopyTechnicianFieldsToEndUser(language.code)
                        }}
                      >
                        <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('troubleshooting_context.copy_from_tech')}`} />
                        {t('copy_from_tech')}
                      </label>
                    )}
                    {language.code !== englishLanguageCode && (
                      <TrashCanFull
                        onClick={(e) => {
                          e.stopPropagation()
                          setLanguages(languages.filter((l) => l.code !== language.code))
                        }}
                      />
                    )}
                  </div>
                }
              >
                <div className="row-wrapper with-sides-no-padding">
                  <div className="input-wrapper">
                    <p className="input-option flex-justify-start">
                      {`${t('language')}*`}
                      <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('troubleshooting_context.language_info')}`} orientation="extra-right" />
                    </p>
                    <Dropdown
                      placeholder={t('please_select')}
                      options={props.languageOptions.map((lang) => ({
                        key: lang,
                        value: `${t(languagesShort[lang.toLowerCase()] || lang)}`,
                        isDisabled: languages.map((l) => l.code).includes(lang) && lang !== language.code,
                      }))}
                      initialSelectedKey={language.code}
                      onChange={(lang) => updateLanguage(language.code, { code: lang.key })}
                      hasError={language.code === unkownLanguageCode}
                      isDisabled={language.code === englishLanguageCode}
                    />
                  </div>
                  <div className="input-wrapper empty" />
                </div>
                <div className="row-wrapper with-sides-no-padding">
                  <div className="input-wrapper">
                    <p className="input-option flex-justify-start">
                      {`${t('description')}`}
                      <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('troubleshooting_context.each_entry')}`} />
                    </p>
                    {language[selectedLanguageTab].descriptions.map((description) => (
                      <div className="d-flex align-center" key={description.id}>
                        <SimpleTextInput
                          value={description.value}
                          onChange={(value) => updateLanguageField(language.code, 'descriptions', description.id, value)}
                          onClear={() => updateLanguageField(language.code, 'descriptions', description.id, '')}
                        />
                        <TrashCanFull
                          className="with-margin-left action-icon"
                          onClick={() => removeLanguageField(language.code, 'descriptions', description.id)}
                        />
                      </div>
                    ))}
                    <button className="add-btn" onClick={() => addLanguageField(language.code, 'descriptions')}>
                      + {t('add_new_description')}
                    </button>
                  </div>
                </div>
                <div className="row-wrapper with-sides-no-padding">
                  <div className="input-wrapper">
                    <p className="input-option flex-justify-start">
                      {`${t('troubleshoot.probable-cause')}`}
                      <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('troubleshooting_context.each_entry')}`} />
                    </p>
                    {language[selectedLanguageTab].probableCauses.map((cause) => (
                      <div className="d-flex align-center" key={cause.id}>
                        <SimpleTextInput
                          value={cause.value}
                          onChange={(value) => updateLanguageField(language.code, 'probableCauses', cause.id, value)}
                          onClear={() => updateLanguageField(language.code, 'probableCauses', cause.id, '')}
                        />
                        <TrashCanFull className="with-margin-left action-icon" onClick={() => removeLanguageField(language.code, 'probableCauses', cause.id)} />
                      </div>
                    ))}
                    <button className="add-btn" onClick={() => addLanguageField(language.code, 'probableCauses')}>
                      + {t('troubleshoot.add_probable_cause')}
                    </button>
                  </div>
                </div>
                {selectedLanguageTab === technicianTabId && (
                  <div className="row-wrapper with-sides-no-padding">
                    <div className="input-wrapper">
                      <p className="input-option flex-justify-start">
                        {`${t('troubleshoot.tech')}`}
                        <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('troubleshooting_context.each_entry')}`} />
                      </p>
                      {language[selectedLanguageTab].techTroubleshootings.map((troubleshoot) => (
                        <div className="d-flex align-center" key={troubleshoot.id}>
                          <SimpleTextInput
                            value={troubleshoot.value}
                            onChange={(value) => updateLanguageField(language.code, 'techTroubleshootings', troubleshoot.id, value)}
                            onClear={() => updateLanguageField(language.code, 'techTroubleshootings', troubleshoot.id, '')}
                          />
                          <TrashCanFull
                            className="with-margin-left action-icon"
                            onClick={() => removeLanguageField(language.code, 'techTroubleshootings', troubleshoot.id)}
                          />
                        </div>
                      ))}
                      <button className="add-btn" onClick={() => addLanguageField(language.code, 'techTroubleshootings')}>
                        + {t('troubleshoot.add_tech_troubleshoot')}
                      </button>
                    </div>
                  </div>
                )}
                <div className="row-wrapper with-sides-no-padding">
                  <div className="input-wrapper">
                    <p className="input-option flex-justify-start">
                      {`${t('troubleshoot.user-troubleshoot')}`}
                      <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('troubleshooting_context.each_entry')}`} />
                    </p>
                    {language[selectedLanguageTab].userTroubleshootings.map((troubleshoot) => (
                      <div className="d-flex align-center" key={troubleshoot.id}>
                        <SimpleTextInput
                          value={troubleshoot.value}
                          onChange={(value) => updateLanguageField(language.code, 'userTroubleshootings', troubleshoot.id, value)}
                          onClear={() => updateLanguageField(language.code, 'userTroubleshootings', troubleshoot.id, '')}
                        />
                        <TrashCanFull
                          className="with-margin-left action-icon"
                          onClick={() => removeLanguageField(language.code, 'userTroubleshootings', troubleshoot.id)}
                        />
                      </div>
                    ))}
                    <button className="add-btn" onClick={() => addLanguageField(language.code, 'userTroubleshootings')}>
                      + {t('troubleshoot.add_user_troubleshoot')}
                    </button>
                  </div>
                </div>
              </ExpandableSection>
            </div>
          ))}
          <div className="row-wrapper">
            <button className="add-btn" onClick={addLanguage}>
              + {t('add_new_language')}
            </button>
          </div>

          <div className="separator" />
          <div className="section-title d-flex flex-justify-start flex-align-center">
            {t('nav.videos').toUpperCase()}
            <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('troubleshooting_context.videos_info')}`} orientation="right" />
          </div>
          <div className="row-wrapper">
            <div className="videos-wrapper">
              {selectedVideos.map((video, index) => (
                <div
                  key={index}
                  className="video-item-wrapper"
                  onClick={() => (video ? setSelectedVideo(video) : setShowAddVideoModal(true))}
                  tabIndex={0}
                  role="button"
                  onKeyDown={(e) => (e.key === keyboardEvents.ENTER && video ? setSelectedVideo(video) : setShowAddVideoModal(true))}
                >
                  <div className="thumbnail-wrapper">
                    <div className="thumbnail-icon">{video ? <VideoPlayIcon /> : <AddSimpleIcon />}</div>
                    {video && <img src={video?.thumbnailUrl} alt="video" />}
                  </div>
                  {video && (
                    <>
                      <p className="title">{video?.title}</p>
                      <p
                        className="add-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedVideos((prevValue) => prevValue.map((v) => (v?.id === video.id ? null : v)))
                        }}
                      >
                        {t('remove')}
                      </p>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </PageModal>
      {showAddVideoModal && (
        <AddFaultVideoModal
          selectedVideos={selectedVideos}
          models={selectedModels}
          handleUpdateVideos={(newVideos) => setSelectedVideos(newVideos)}
          closeModal={() => setShowAddVideoModal(false)}
        />
      )}
      {selectedVideo && <VideoModal title={selectedVideo.title} videoId={selectedVideo.videoId} onCloseVideo={() => setSelectedVideo(null)} />}
    </>
  )
}

function stateToProps({ troubleshooting, shared }) {
  return {
    languageOptions: shared?.languages?.map((l) => l.languageCode) || [],
    models: [...new Set(shared?.models?.filter((m) => m.ocp_access)?.map((model) => model.model) || [])],
    faultUserGroups: troubleshooting?.faultUserGroups || [],
    selectedFault: troubleshooting?.faultDetails,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        createFault,
        updateFault,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(AddEditFaultModal)

